import React, { useState } from 'react';
import { PresentationOutline, SlideOutline } from '../services/SlidePlanService';

interface SlidePlanEditorProps {
  outline: PresentationOutline;
  onSave: (outline: PresentationOutline) => void;
  onGenerate: (outline: PresentationOutline) => Promise<string>;
  onCancel: () => void;
  isLoadedFromFirestore?: boolean;
}

const SlidePlanEditor: React.FC<SlidePlanEditorProps> = ({ 
  outline, 
  onSave, 
  onGenerate,
  onCancel,
  isLoadedFromFirestore = false
}) => {
  const [editedOutline, setEditedOutline] = useState<PresentationOutline>(outline);
  const [selectedSlide, setSelectedSlide] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');

  // Handle slide content changes
  const handleSlideChange = (index: number, updatedSlide: SlideOutline) => {
    const newSlides = [...editedOutline.slides];
    newSlides[index] = updatedSlide;
    setEditedOutline({
      ...editedOutline,
      slides: newSlides
    });
  };

  // Add a new slide
  const handleAddSlide = (index: number) => {
    // Determine appropriate slide type and content based on position
    let slideType = 'content';
    let slideTitle = '';
    let slideContent = '';
    
    const currentLength = editedOutline.slides.length;
    
    // If adding at the beginning, make it a title slide if none exists
    if (index === 0 && !editedOutline.slides.some(slide => slide.slideType === 'title')) {
      slideType = 'title';
      slideTitle = editedOutline.presentationTitle;
      slideContent = `Title slide for ${editedOutline.presentationTitle}\nInclude subtitle, presenter information, and date`;
    } 
    // If adding at the end, consider making it a conclusion slide if none exists
    else if (index === currentLength - 1 && !editedOutline.slides.some(slide => slide.slideType === 'conclusion')) {
      slideType = 'conclusion';
      slideTitle = 'Summary and Conclusion';
      slideContent = 'Summarize key points from the presentation and provide any closing thoughts or action items.';
    }
    // Otherwise make it a standard content slide
    else {
      // If adding after a specific slide, make the content relevant
      if (index < currentLength) {
        const prevSlide = editedOutline.slides[index];
        slideType = 'content';
        slideTitle = `Supporting Content for ${prevSlide.title}`;
        slideContent = `Additional information related to ${prevSlide.title}.\n- Key point 1\n- Key point 2\n- Key point 3`;
      } else {
        slideType = 'content';
        slideTitle = `Content Slide ${currentLength + 1}`;
        slideContent = 'Main content for this slide with supporting details and examples.';
      }
    }
    
    const newSlide: SlideOutline = {
      slideNumber: currentLength + 1,
      slideType: slideType,
      title: slideTitle,
      content: slideContent
    };
    
    const newSlides = [...editedOutline.slides];
    newSlides.splice(index + 1, 0, newSlide);
    
    // Update slide numbers
    const reorderedSlides = newSlides.map((slide, idx) => ({
      ...slide,
      slideNumber: idx + 1
    }));
    
    setEditedOutline({
      ...editedOutline,
      slides: reorderedSlides
    });
  };

  // Remove a slide
  const handleRemoveSlide = (index: number) => {
    const newSlides = editedOutline.slides.filter((_, idx) => idx !== index);
    
    // Update slide numbers
    const reorderedSlides = newSlides.map((slide, idx) => ({
      ...slide,
      slideNumber: idx + 1
    }));
    
    setEditedOutline({
      ...editedOutline,
      slides: reorderedSlides
    });
    
    if (selectedSlide === index) {
      setSelectedSlide(null);
    }
  };

  // Move a slide up
  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newSlides = [...editedOutline.slides];
      [newSlides[index - 1], newSlides[index]] = [newSlides[index], newSlides[index - 1]];
      
      // Update slide numbers
      const reorderedSlides = newSlides.map((slide, idx) => ({
        ...slide,
        slideNumber: idx + 1
      }));
      
      setEditedOutline({
        ...editedOutline,
        slides: reorderedSlides
      });
      
      if (selectedSlide === index) {
        setSelectedSlide(index - 1);
      } else if (selectedSlide === index - 1) {
        setSelectedSlide(index);
      }
    }
  };

  // Move a slide down
  const handleMoveDown = (index: number) => {
    if (index < editedOutline.slides.length - 1) {
      const newSlides = [...editedOutline.slides];
      [newSlides[index], newSlides[index + 1]] = [newSlides[index + 1], newSlides[index]];
      
      // Update slide numbers
      const reorderedSlides = newSlides.map((slide, idx) => ({
        ...slide,
        slideNumber: idx + 1
      }));
      
      setEditedOutline({
        ...editedOutline,
        slides: reorderedSlides
      });
      
      if (selectedSlide === index) {
        setSelectedSlide(index + 1);
      } else if (selectedSlide === index + 1) {
        setSelectedSlide(index);
      }
    }
  };

  // Handle the title change
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedOutline({
      ...editedOutline,
      presentationTitle: e.target.value
    });
  };

  // Handle the summary change
  const handleSummaryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setEditedOutline({
      ...editedOutline,
      presentationSummary: e.target.value
    });
  };

  // Generate the slides from outline
  const handleGenerateSlides = async () => {
    setIsGenerating(true);
    setGenerationProgress('Starting generation...');
    
    try {
      await onGenerate(editedOutline);
    } catch (error) {
      console.error('Error generating slides:', error);
      setGenerationProgress('Error generating slides');
    } finally {
      setIsGenerating(false);
    }
  };

  // Get background color based on slide type
  const getSlideTypeColor = (slideType: string) => {
    switch (slideType.toLowerCase()) {
      case 'title':
        return 'bg-blue-100';
      case 'agenda':
        return 'bg-purple-100';
      case 'section':
        return 'bg-indigo-100';
      case 'content':
        return 'bg-gray-100';
      case 'image':
        return 'bg-green-100';
      case 'chart':
        return 'bg-yellow-100';
      case 'quote':
        return 'bg-orange-100';
      case 'conclusion':
        return 'bg-red-100';
      default:
        return 'bg-gray-100';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold">{editedOutline.presentationTitle}</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            {editedOutline.slides.length} slides • 
            <span className="ml-1">
              {isLoadedFromFirestore && (
                <span className="inline-flex items-center ml-1 text-green-600 dark:text-green-500">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  Loaded from saved plan
                </span>
              )}
            </span>
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              onSave(editedOutline);
              onCancel();
            }}
            className="px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
            Exit Editor
          </button>
          <button
            onClick={() => onSave(editedOutline)}
            className="px-4 py-2 text-sm bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/30 dark:hover:bg-blue-800/50 text-blue-700 dark:text-blue-400 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"></path>
            </svg>
            Save Plan
          </button>
          <button
            onClick={handleGenerateSlides}
            disabled={isGenerating}
            className={`px-4 py-2 rounded-lg text-white text-sm transition-colors flex items-center premium-button ${
              isGenerating 
                ? 'bg-gray-400 dark:bg-gray-700 cursor-not-allowed'
                : 'bg-gradient-to-r from-blue-700 to-indigo-800 hover:from-blue-600 hover:to-indigo-700 shadow-md premium-shadow'
            }`}
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {generationProgress || 'Generating...'}
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                </svg>
                Generate Presentation
              </>
            )}
            <div className="absolute top-0 -inset-full h-full w-1/2 z-5 block transform -skew-x-12 bg-gradient-to-r from-transparent to-white opacity-20 group-hover:animate-shine" />
          </button>
        </div>
      </div>

      <div className="flex h-[calc(100vh-12rem)]">
        {/* Slide list sidebar */}
        <div className="w-72 border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-850 overflow-y-auto">
          <div className="p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <svg className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                Presentation Title
              </label>
              <input
                type="text"
                value={editedOutline.presentationTitle}
                onChange={handleTitleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 dark:focus:ring-blue-500/20 dark:focus:border-blue-500/30 premium-input"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <svg className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"></path>
                </svg>
                Presentation Summary
              </label>
              <textarea
                value={editedOutline.presentationSummary}
                onChange={handleSummaryChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 dark:focus:ring-blue-500/20 dark:focus:border-blue-500/30 premium-input"
              />
            </div>
          </div>
          
          <div className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-850 px-4 py-2 border-b border-gray-200 dark:border-gray-800">
            <div className="flex justify-between items-center">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Slides ({editedOutline.slides.length})
              </h3>
              <button
                className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center"
                onClick={() => handleAddSlide(editedOutline.slides.length - 1)}
              >
                <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                </svg>
                Add Slide
              </button>
            </div>
          </div>
          
          <div className="px-2 py-2 space-y-1.5">
            {editedOutline.slides.map((slide, index) => (
              <div key={index} className="relative group">
                <button
                  className={`w-full px-2 py-2 text-left rounded-md text-sm group flex items-center ${
                    selectedSlide === index 
                    ? 'bg-blue-100/80 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300 border-l-2 border-blue-500 dark:border-blue-400 pl-1.5' 
                    : 'hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 pl-2'
                  }`}
                  onClick={() => setSelectedSlide(index)}
                >
                  <span className={`w-6 h-6 flex-shrink-0 flex items-center justify-center ${getSlideTypeColor(slide.slideType)} dark:bg-opacity-30 rounded mr-2 text-xs font-medium ring-1 ring-gray-300/50 dark:ring-gray-700/50`}>
                    {slide.slideNumber}
                  </span>
                  <span className="truncate flex-1">{slide.title}</span>
                  
                  <div className="flex ml-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {index > 0 && (
                      <button
                        className="text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 p-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveUp(index);
                        }}
                        title="Move up"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                        </svg>
                      </button>
                    )}
                    {index < editedOutline.slides.length - 1 && (
                      <button
                        className="text-gray-500 hover:text-blue-500 dark:text-gray-400 dark:hover:text-blue-400 p-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMoveDown(index);
                        }}
                        title="Move down"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                        </svg>
                      </button>
                    )}
                    <button
                      className="text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveSlide(index);
                      }}
                      title="Remove slide"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </button>
                
                <button
                  className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-5 h-5 rounded-full bg-white dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center justify-center opacity-0 group-hover:opacity-100 z-10 border border-gray-300 dark:border-gray-600 transition-opacity shadow-sm"
                  onClick={() => handleAddSlide(index)}
                  title="Add slide after this one"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
        
        {/* Slide editor */}
        <div className="flex-1 overflow-y-auto bg-white dark:bg-gray-900">
          {selectedSlide !== null ? (
            <div className="p-6">
              <div className="flex mb-6">
                <div className={`w-14 h-14 rounded-lg ${getSlideTypeColor(editedOutline.slides[selectedSlide].slideType)} dark:bg-opacity-30 flex items-center justify-center text-blue-800 dark:text-blue-300 mr-4 shadow-sm`}>
                  <span className="text-xl font-bold">{editedOutline.slides[selectedSlide].slideNumber}</span>
                </div>
                <div>
                  <h3 className="text-xl font-medium text-gray-900 dark:text-white">
                    {editedOutline.slides[selectedSlide].title || 'Untitled Slide'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {editedOutline.slides[selectedSlide].slideType.charAt(0).toUpperCase() + editedOutline.slides[selectedSlide].slideType.slice(1)} Slide
                  </p>
                </div>
              </div>
            
              <div className="grid grid-cols-12 gap-6">
                <div className="col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Slide Number
                  </label>
                  <input
                    type="number"
                    value={editedOutline.slides[selectedSlide].slideNumber}
                    onChange={(e) => handleSlideChange(selectedSlide, {
                      ...editedOutline.slides[selectedSlide],
                      slideNumber: parseInt(e.target.value) || 0
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 premium-input"
                  />
                </div>
                <div className="col-span-9">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Slide Type
                  </label>
                  <select
                    value={editedOutline.slides[selectedSlide].slideType}
                    onChange={(e) => handleSlideChange(selectedSlide, {
                      ...editedOutline.slides[selectedSlide],
                      slideType: e.target.value
                    })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 premium-input"
                  >
                    <option value="title">Title Slide</option>
                    <option value="agenda">Agenda</option>
                    <option value="section">Section Divider</option>
                    <option value="content">Content Slide</option>
                    <option value="chart">Chart/Diagram</option>
                    <option value="quote">Quote/Testimonial</option>
                    <option value="image">Image/Visual</option>
                    <option value="conclusion">Conclusion</option>
                    <option value="qa">Q&A</option>
                    <option value="appendix">Appendix</option>
                  </select>
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Slide Title
                </label>
                <input
                  type="text"
                  value={editedOutline.slides[selectedSlide].title}
                  onChange={(e) => handleSlideChange(selectedSlide, {
                    ...editedOutline.slides[selectedSlide],
                    title: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 premium-input"
                  placeholder="Enter a concise and descriptive title"
                />
              </div>
              
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <span>Slide Content</span>
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded">Main Content</span>
                </label>
                <textarea
                  value={editedOutline.slides[selectedSlide].content}
                  onChange={(e) => handleSlideChange(selectedSlide, {
                    ...editedOutline.slides[selectedSlide],
                    content: e.target.value
                  })}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 premium-input"
                  placeholder="Enter the main content points for this slide"
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 flex items-start">
                  <svg className="w-4 h-4 mr-1 mt-0.5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Include key points, data highlights, and strategic insights to convey in this slide.
                </p>
              </div>
              
              <div className="mt-6 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-800">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                  <span>Speaker Notes</span>
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded">Presenter Only</span>
                </label>
                <textarea
                  value={editedOutline.slides[selectedSlide].notes || ''}
                  onChange={(e) => handleSlideChange(selectedSlide, {
                    ...editedOutline.slides[selectedSlide],
                    notes: e.target.value
                  })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 premium-input"
                  placeholder="Add detailed notes for the presenter (not shown in the actual slides)"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  These notes will be available in presentation mode but won't appear on the slides themselves.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-6">
              <div className="text-center max-w-md">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 blur-2xl opacity-70 rounded-full transform -translate-y-4"></div>
                  <div className="relative bg-blue-100 dark:bg-blue-900/20 rounded-full p-6 mb-4 inline-block shadow-sm border border-blue-200 dark:border-blue-800/30">
                    <svg className="w-12 h-12 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-medium text-gray-900 dark:text-white mb-2">
                  Structure Your Presentation
                </h3>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                  Select a slide from the sidebar to edit its content, or add new slides to build your presentation narrative.
                </p>
                <button
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg shadow-sm transition-colors flex items-center mx-auto premium-button premium-shadow"
                  onClick={() => {
                    if (editedOutline.slides.length > 0) {
                      setSelectedSlide(0);
                    } else {
                      handleAddSlide(0);
                      setTimeout(() => setSelectedSlide(0), 100);
                    }
                  }}
                >
                  <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122"></path>
                  </svg>
                  Begin Editing
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Key controls tip */}
      <div className="absolute bottom-4 right-4 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 shadow-md rounded-lg border border-gray-200 dark:border-gray-700 p-2">
        <div className="flex items-center">
          <span>Keyboard shortcuts:</span>
          <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 mx-1">Tab</span>
          <span>Next field</span>
          <span className="ml-2 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 mx-1">Ctrl+S</span>
          <span>Save</span>
        </div>
      </div>
    </div>
  );
};

export default SlidePlanEditor; 